/**
 * Validation Service
 * Xử lý tất cả validation logic cho base form
 */

import { Injectable } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { FieldConfig, ValidationConfig, PresetValidation } from '../models/field-config.interface';
import { DEFAULT_VALIDATION_MESSAGES } from '../constants/validation-constants';
import { getPresetValidator } from '../validators/custom-validators';

@Injectable({
    providedIn: 'root'
})
export class ValidationService {

    /**
     * Build validators array từ ValidationConfig
     */
    buildValidators(config: ValidationConfig): ValidatorFn[] {
        const validators: ValidatorFn[] = [];

        // Required
        if (config.required) {
            validators.push(Validators.required);
        }

        // Min Length
        if (config.minLength) {
            const minLength = typeof config.minLength === 'number'
                ? config.minLength
                : config.minLength.value;
            validators.push(Validators.minLength(minLength));
        }

        // Max Length
        if (config.maxLength) {
            const maxLength = typeof config.maxLength === 'number'
                ? config.maxLength
                : config.maxLength.value;
            validators.push(Validators.maxLength(maxLength));
        }

        // Min Value
        if (config.min !== undefined) {
            const min = typeof config.min === 'number' ? config.min : config.min.value;
            validators.push(Validators.min(min));
        }

        // Max Value
        if (config.max !== undefined) {
            const max = typeof config.max === 'number' ? config.max : config.max.value;
            validators.push(Validators.max(max));
        }

        // Email
        if (config.email) {
            validators.push(Validators.email);
        }

        // Pattern
        if (config.pattern) {
            const pattern = typeof config.pattern === 'string' || config.pattern instanceof RegExp
                ? config.pattern
                : config.pattern.value;
            validators.push(Validators.pattern(pattern));
        }

        // Preset Validation
        if (config.preset) {
            const presetValidator = getPresetValidator(config.preset);
            if (presetValidator) {
                validators.push(presetValidator);
            }
        }

        // Custom Validators
        if (config.customValidators && config.customValidators.length > 0) {
            validators.push(...config.customValidators);
        }

        return validators;
    }

    /**
     * Lấy error message cho một control
     */
    getErrorMessage(control: AbstractControl, fieldConfig: FieldConfig): string {
        if (!control.errors) {
            return '';
        }

        const errors = control.errors;
        const customMessages = fieldConfig.validation?.messages || {};

        // Check từng loại error theo thứ tự ưu tiên
        for (const errorKey of Object.keys(errors)) {
            const message = this.getMessageForError(errorKey, errors[errorKey], fieldConfig, customMessages);
            if (message) {
                return message;
            }
        }

        return 'Giá trị không hợp lệ';
    }

    /**
     * Lấy message cho một error cụ thể
     */
    private getMessageForError(
        errorKey: string,
        errorValue: any,
        fieldConfig: FieldConfig,
        customMessages: any
    ): string {
        // 1. Check custom message trong validation config
        if (customMessages[errorKey]) {
            return this.interpolateMessage(customMessages[errorKey], errorValue);
        }

        // 2. Check custom messages object
        if (customMessages.custom && customMessages.custom[errorKey]) {
            return this.interpolateMessage(customMessages.custom[errorKey], errorValue);
        }

        // 3. Check inline message trong validation config
        const validationConfig = fieldConfig.validation;
        if (validationConfig) {
            const configValue = (validationConfig as any)[errorKey];
            if (configValue && typeof configValue === 'object' && configValue.message) {
                return this.interpolateMessage(configValue.message, errorValue);
            }
        }

        // 4. Use default messages
        const defaultMessage = (DEFAULT_VALIDATION_MESSAGES as any)[errorKey];
        if (defaultMessage) {
            return this.interpolateMessage(defaultMessage, errorValue);
        }

        return 'Giá trị không hợp lệ';
    }

    /**
     * Thay thế placeholders trong message
     * Ví dụ: "Tối thiểu {minLength} ký tự" => "Tối thiểu 5 ký tự"
     */
    private interpolateMessage(message: string, errorValue: any): string {
        if (!errorValue || typeof errorValue !== 'object') {
            return message;
        }

        let result = message;

        // Common placeholders
        const placeholders: { [key: string]: string } = {
            '{minLength}': errorValue.requiredLength || '',
            '{maxLength}': errorValue.requiredLength || '',
            '{min}': errorValue.min || '',
            '{max}': errorValue.max || '',
            '{actual}': errorValue.actualLength || errorValue.actual || '',
            '{pattern}': errorValue.requiredPattern || '',
        };

        for (const [placeholder, value] of Object.entries(placeholders)) {
            result = result.replace(placeholder, String(value));
        }

        return result;
    }

    /**
     * Check xem field có required không
     */
    isRequired(fieldConfig: FieldConfig): boolean {
        if (!fieldConfig.validation) {
            return false;
        }

        const required = fieldConfig.validation.required;
        if (typeof required === 'boolean') {
            return required;
        }
        if (typeof required === 'object') {
            return required.value;
        }
        return false;
    }

    /**
     * Mark all controls as touched để hiển thị errors
     */
    markAllAsTouched(form: FormGroup): void {
        Object.keys(form.controls).forEach(key => {
            const control = form.get(key);
            if (control instanceof FormControl) {
                control.markAsTouched();
            } else if (control instanceof FormGroup) {
                this.markAllAsTouched(control);
            }
        });
    }

    /**
     * Check form có valid không
     */
    isFormValid(form: FormGroup): boolean {
        return form.valid;
    }

    /**
     * Get all errors từ form
     */
    getAllErrors(form: FormGroup): { [key: string]: any } {
        const errors: { [key: string]: any } = {};

        Object.keys(form.controls).forEach(key => {
            const control = form.get(key);
            if (control?.errors) {
                errors[key] = control.errors;
            }
        });

        return errors;
    }
}
